import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

export type DriverVehicleTab = 'driver' | 'vehicle'

const TAB_PARAM = 'tab'

export function useDriverVehicleTab() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: DriverVehicleTab =
    searchParams.get(TAB_PARAM) === 'vehicle' ? 'vehicle' : 'driver'

  const setTab = useCallback(
    (next: DriverVehicleTab) =>
      setSearchParams(
        params => {
          params.set(TAB_PARAM, next)
          return params
        },
        { replace: true }
      ),
    [setSearchParams]
  )

  return [tab, setTab] as const
}

interface Props {
  tab: DriverVehicleTab
  onChange: (tab: DriverVehicleTab) => void
}

export const DriverVehicleTabs: React.FC<Props> = ({ tab, onChange }) => {
  const translations = useTranslate({
    drivers: 'common.drivers',
    vehicles: 'common.vehicles',
  })

  const tabs: { key: DriverVehicleTab; label: string }[] = [
    { key: 'driver', label: translations.drivers },
    { key: 'vehicle', label: translations.vehicles },
  ]

  return (
    <div
      role="tablist"
      className="mb-4 inline-flex gap-1 rounded-xl bg-stone-100 p-1"
    >
      {tabs.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={tab === key}
          onClick={() => onChange(key)}
          className={cn(
            'rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors',
            tab === key
              ? 'bg-white text-stone-900 shadow-sm'
              : 'text-stone-600 hover:text-stone-900'
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}
